import React from 'react';
import { Droplets, Zap, Shield, CheckCircle, Phone } from 'lucide-react';
import watercommunity from './img/tank.png';
import gridsolar from './img/gridsolar.png';
import cctvs from './img/cctvstate.png';
import assestment from './img/assesment.png'
import installation from './img/support.png'
import support from './img/support2.png'

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: "Community Water Project",
      description: "Solar-powered water systems for rural communities, improving access and sustainability in Agriculture Health and Education",
      icon: <Droplets className="w-8 h-8" />,
      category: "Solar Water Systems",
      impact: "Provided clean water access to 150+ households",
      features: [
        "Solar-powered water pumps",
        "Storage tank systems",
        "Remote monitoring",
        "Maintenance training"
      ],
      image: watercommunity,
      results: {
        households: "150+",
        waterSupplied: "10,000L daily",
        energySaved: "80%"
      }
    },
    {
      id: 2,
      title: "Grid-tie Solar Systems",
      description: "Large-scale solar installations for businesses and institutions, reducing grid dependence",
      icon: <Zap className="w-8 h-8" />,
      category: "Commercial Solar",
      impact: "Reduced electricity costs by 70% for participating businesses",
      features: [
        "High-capacity solar panels",
        "Grid-tie inverters",
        "Energy monitoring systems",
        "Net metering setup"
      ],
      image: gridsolar,
      results: {
        businesses: "30+",
        energyGenerated: "950kW",
        costReduction: "70%"
      }
    },
    {
      id: 3,
      title: "CCTV for Estates",
      description: "Secure residential compounds with modern surveillance and remote monitoring",
      icon: <Shield className="w-8 h-8" />,
      category: "Security Systems",
      impact: "Enhanced security for residential estates with 24/7 monitoring",
      features: [
        "HD IP cameras",
        "Remote monitoring",
        "Motion detection",
        "Mobile app access"
      ],
      image: cctvs,
      results: {
        estates: "15+",
        cameras: "1000+",
        incidents: "90% reduction"
      }
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#fafafa' }}>
      {/* Projects Grid */}
      <section className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
              Our Work
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-3" style={{ color: '#2a2118' }}>
              Featured Projects
            </h2>
            <p className="text-base font-light max-w-2xl mx-auto" style={{ color: '#5a524c' }}>
              Discover how we're making a difference in communities and businesses
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {projects.map((project) => (
              <div key={project.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                </div>

                <div className="p-6">
                  <span className="text-xs font-semibold tracking-wide uppercase px-2.5 py-1 rounded-full" style={{ background: '#712B35', color: 'white' }}>
                    {project.category}
                  </span>
                  
                  <h3 className="text-lg font-bold mt-3 mb-2" style={{ color: '#2a2118' }}>{project.title}</h3>
                  <p className="text-sm leading-relaxed mb-3 font-light" style={{ color: '#5a524c' }}>{project.description}</p>

                  <div className="mb-4">
                    <div className="flex items-center gap-1.5 mb-2">
                      <CheckCircle className="w-4 h-4" style={{ color: '#489820' }} />
                      <span className="text-sm font-semibold" style={{ color: '#489820' }}>Impact Achieved</span>
                    </div>
                    <p className="text-sm" style={{ color: '#5a524c' }}>{project.impact}</p>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm font-semibold mb-2" style={{ color: '#2a2118' }}>Key Features:</p>
                    <ul className="text-sm space-y-1" style={{ color: '#5a524c' }}>
                      {project.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#712B35' }} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="border-t pt-4" style={{ borderColor: '#f3f4f6' }}>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      {Object.entries(project.results).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-lg font-bold" style={{ color: '#712B35' }}>{value}</div>
                          <div className="text-xs capitalize font-medium" style={{ color: '#9ca3af' }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Stats */}
      <section className="py-20 px-6 md:px-10 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
              Our Impact
            </span>
            <h2 className="text-2xl md:text-3xl font-bold mt-3 mb-2" style={{ color: '#2a2118' }}>
              Numbers That Speak
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { number: "40+", label: "Projects Completed" },
              { number: "500+", label: "Families Served" },
              { number: "1MW+", label: "Solar Energy Installed" },
              { number: "75%", label: "Average Cost Reduction" }
            ].map((stat, index) => (
              <div key={index} className="text-center p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300" style={{ background: '#fafafa' }}>
                <div className="text-4xl font-bold mb-2" style={{ color: '#712B35' }}>{stat.number}</div>
                <div className="text-sm font-medium" style={{ color: '#5a524c' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#712B35' }}>
              How We Work
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-3" style={{ color: '#2a2118' }}>
              Our Process
            </h2>
            <p className="text-base font-light max-w-2xl mx-auto" style={{ color: '#5a524c' }}>
              How we deliver successful projects with precision and care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { img: assestment, num: "1", title: "Assessment", desc: "We evaluate your needs and site conditions to design the perfect solution" },
              { img: installation, num: "2", title: "Installation", desc: "Our skilled technicians install your system with precision and care" },
              { img: support, num: "3", title: "Support", desc: "We provide ongoing maintenance and 24/7 support for your peace of mind" }
            ].map((step, index) => (
              <div key={index} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img src={step.img} alt={step.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute top-4 right-4 w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white shadow-lg transition-transform duration-300 group-hover:rotate-12"
                    style={{ background: '#712B35' }}>
                    {step.num}
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#2a2118' }}>{step.title}</h3>
                  <p className="leading-relaxed font-light" style={{ color: '#5a524c' }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-10" style={{ background: '#712B35' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Start Your Project?</h2>
          <p className="text-lg text-white/80 mb-6 font-light">Join the growing list of satisfied customers who trust Torchbearer Technologies</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:0743076376" className="bg-white px-8 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center justify-center gap-2" style={{ color: '#712B35' }}>
              <Phone className="w-5 h-5" />
              Call Now: 0743076376
            </a>
            <a href="mailto:torchbearertechnologies@gmail.com" className="border-2 border-white text-white px-8 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:bg-white hover:text-[#712B35] flex items-center justify-center gap-2">
              Get a Quote
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
